import { CommonModule, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ContentChild,
  EventEmitter,
  HostListener,
  Input,
  Output,
  signal,
  TemplateRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ListboxModule } from 'primeng/listbox';

// ─── Default item model (listbox use-case) ─────────────────────────────────────

/**
 * Shape expected by the built-in default item template.
 * Consumers who supply their own `<ng-template>` may use any item type.
 */
export interface ListboxItem {
  /** Material icon name shown before the badge. Defaults to `"info"`. */
  icon?: string;
  /** Optional count pill rendered after the icon. */
  count?: number;
  /** Primary text shown in the row. */
  message: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Multi-column listbox built on top of a single PrimeNG `p-listbox`.
 *
 * Columns are formed purely via CSS grid (`grid-auto-flow: column`) so that
 * p-listbox keyboard navigation and ARIA semantics remain intact.
 * When `multiColumn` is `true` items wrap into a new column after every
 * `columnSize` rows; the wrapper becomes horizontally scrollable when the
 * content is wider than the container.
 *
 *
 * ### Custom template
 * Project an `<ng-template let-item>` child to fully control row rendering.
 *
 * @example — native rendering
 * ```html
 * <cbre-listbox [items]="items" optionLabel="name" [multiColumn]="true"></cbre-listbox>
 * ```
 *
 * @example — custom template
 * ```html
 * <cbre-listbox [items]="errors" [multiple]="true">
 *   <ng-template let-item>
 *     <mat-icon>{{ item.icon || 'info' }}</mat-icon>
 *     <span>{{ item.message }}</span>
 *   </ng-template>
 * </cbre-listbox>
 * ```
 */
@Component({
  selector: 'cbre-listbox',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, NgIf, NgTemplateOutlet, FormsModule, ListboxModule],
  styleUrls: ['./listbox.component.scss'],
  template: `
    <div class="cbre-listbox">
      <div [class.cbre-listbox__columns-wrapper]="multiColumn()" [style.--cbre-rows-per-column]="columnSize()">
        <p-listbox
          [options]="items()"
          [optionLabel]="optionLabel()"
          [optionValue]="optionValue()"
          [styleClass]="customItemTemplate ? 'cbre-listbox__list is-projected' : 'cbre-listbox__list'"
          [multiple]="multiple()"
          datakey="datakey()"
          [metaKeySelection]="metaKeySelection()"
          [ngModel]="selectedItems()"
          (onChange)="onSelectionChange($event.value)"
        >
          <ng-template *ngIf="customItemTemplate" let-item pTemplate="item">
            <ng-container *ngTemplateOutlet="customItemTemplate; context: { $implicit: item }"></ng-container>
          </ng-template>
        </p-listbox>
      </div>
    </div>
  `,
})
export class CbreListboxComponent<T = ListboxItem> {
  /**
   * Items to display. Accepts any type; the built-in template expects
   * objects conforming to `ListboxItem`.
   */
  readonly items = signal<T[]>([]);
  @Input('items') set itemsInput(val: T[]) {
    this.items.set(val);
  }

  /**
   * Field name used by p-listbox for its selection label.
   * Set to `null` when using fully custom templates that don't need it.
   */
  readonly optionLabel = signal<string | null>(null);
  @Input('optionLabel') set optionLabelInput(val: string | null) {
    this.optionLabel.set(val);
  }

  readonly datakey = signal<string | null>(null);
  @Input('datakey') set datakeyInput(val: string | null) {
    this.datakey.set(val);
  }

  /**
   * Field name used by p-listbox as the value key for selection.
   * When set, PrimeNG stores and compares selections by this primitive field
   * instead of object identity — so `p-highlight` survives item re-creation
   * (e.g. when a signal emits a new array of new objects with the same data).
   * `selectionChange` still emits full item objects resolved from the current list.
   * Set to `null` (default) to use full object identity (original behaviour).
   */
  readonly optionValue = signal<string | null>(null);
  @Input('optionValue') set optionValueInput(val: string | null) {
    this.optionValue.set(val);
  }

  /** Maximum number of rows per column. When `multiColumn` is true items wrap after this many rows. Default: 5.
   *  Ignored when `responsiveColumnSize` is true — the component will derive this from the viewport height. */
  readonly _columnSizeOverride = signal<number | null>(5);
  @Input('columnSize') set columnSizeInput(val: number) {
    this._columnSizeOverride.set(val);
  }

  private readonly _windowHeight = signal(typeof window !== 'undefined' ? window.innerHeight : 900);

  @HostListener('window:resize')
  onResize(): void {
    this._windowHeight.set(window.innerHeight);
  }

  /**
   * Resolved column size.
   * Priority: explicit [columnSize] input → responsive (if enabled) → default (5).
   */
  readonly columnSize = computed<number>(() => {
    const screenHeight = this._windowHeight();
    if (screenHeight < 750) return 2;
    if (screenHeight < 900) return 4;

    return this._columnSizeOverride() ?? 5;
  });

  /** Enable multi-column layout. Items flow into a new column after every `columnSize` rows. */
  readonly multiColumn = signal(false);
  @Input('multiColumn') set multiColumnInput(val: boolean) {
    this.multiColumn.set(val);
  }

  /** Whether to allow multiple selection. */
  readonly multiple = signal(false);
  @Input('multiple') set multipleInput(val: boolean) {
    this.multiple.set(val);
  }

  /**
   * Raw selected keys held by the consumer (primitive values when `optionValue` is set,
   * full objects otherwise). Updated both by the consumer's `[selectedItems]` binding
   * and internally by `onSelectionChange`.
   *
   * Keeping this as a separate writable signal — instead of conflating it with
   * `selectedItems` — lets `selectedItems` be a pure `computed` that always re-derives
   * from both `_selectedKeys` AND `items()`. That way, every time `items()` re-emits a
   * new array (even with identical data), `selectedItems` produces a new array reference,
   * which forces Angular's NgModel to call `writeValue` and PrimeNG to re-evaluate
   * `isSelected` — keeping `p-highlight` intact after item-list refreshes.
   */
  private readonly _selectedKeys = signal<any>(null);
  @Input('selectedItems') set selectedItemsInput(val: T | T[]) {
    this._selectedKeys.set(val);
  }

  /**
   * Computed selection value passed to `[ngModel]` on `p-listbox`.
   * Re-evaluates whenever `_selectedKeys` or `items()` changes, guaranteeing a new
   * array reference on every items refresh so Angular always calls `writeValue` and
   * PrimeNG re-evaluates `isSelected` — keeping `p-highlight` intact after item-list
   * refreshes (e.g. when a count decrements and `legendItems()` re-emits new objects).
   *
   * When `optionValue` is set, PrimeNG internally extracts the primitive key from each
   * option and compares it to the model entries — so the model must contain the same
   * primitive keys that `_selectedKeys` already holds. We still need to rebuild the
   * array every time `items()` changes to force a new reference; the content (keys)
   * stays identical.
   */
  readonly selectedItems = computed<any>(() => {
    const keys = this._selectedKeys();
    // Touch items() so this computed re-runs whenever the list changes,
    // producing a new array reference even if the keys themselves didn't change.
    // This is the mechanism that forces Angular's NgModel to call writeValue.
    void this.items();

    if (keys == null) {
      return keys;
    }

    // Re-wrap in a fresh array to guarantee a new reference each time.
    return Array.isArray(keys) ? [...keys] : keys;
  });

  readonly metaKeySelection = signal(false);
  @Input('metaKeySelection') set metaKeySelectionInput(val: boolean) {
    this.metaKeySelection.set(val);
  }

  /** Emits the selected item(s) on change. Emits `T` when `multiple=false`, `T[]` when `multiple=true`. */
  @Output() selectionChange = new EventEmitter<T | T[]>();

  /**
   * Optional custom row template projected by the consumer.
   * The template receives each item as the implicit context variable.
   *
   * ```html
   * <cbre-listbox [items]="rows">
   *   <ng-template let-row>{{ row.name }}</ng-template>
   * </cbre-listbox>
   * ```
   */
  @ContentChild(TemplateRef) customItemTemplate: TemplateRef<{ $implicit: T }> | null = null;

  onSelectionChange(value: T | T[]): void {
    let selection: T | T[];

    if (this.multiple()) {
      selection = ([] as T[]).concat(value ?? []);
    } else {
      selection = Array.isArray(value) ? value[0] : value;
    }

    // Persist the raw keys (primitives when optionValue is set, objects otherwise)
    // so that selectedItems computed can re-resolve them against the latest items().
    this._selectedKeys.set(selection);

    // When optionValue is set, p-listbox emits primitive keys instead of objects.
    // Resolve back to full items so consumers always receive item objects.
    const key = this.optionValue();
    if (key) {
      const currentItems = this.items();
      if (Array.isArray(selection)) {
        const resolved = (selection as any[])
          .map(v => currentItems.find(item => (item as any)[key] === v))
          .filter(Boolean) as T[];
        this.selectionChange.emit(resolved);
      } else {
        const resolved = currentItems.find(item => (item as any)[key] === selection) ?? null;
        this.selectionChange.emit(resolved as T);
      }
      return;
    }

    this.selectionChange.emit(selection);
  }
}
